import { useState, useMemo } from "react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  useGetProviderAppointments,
  useGetGroupAppointments,
  useUpdateAppointmentStatus,
  useStartAppointment,
  useListProviderEstimates,
  useGetProviderAppointmentFilterOptions,
  API_BASE
} from "@/lib/api-client-react";
import {
  CheckCircle2, XCircle, Search, ChevronLeft, ChevronRight,
  Calendar as CalendarIcon, Mail, Phone, MapPin, Eye, X,
  User, Package, Stethoscope, DollarSign, Tag, FileText,
  Hash, Building2, Globe, Clock, Loader2, Play, Key, Edit3,
  Filter, LayoutList, LayoutGrid, AlertCircle, ArrowUpDown, CheckSquare, Sparkles
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Helper to extract service rendered date from notes
function getServiceRenderedDate(notes?: string): string | null {
  if (!notes) return null;
  const match = notes.match(/\[SERVICE_RENDERED:([^\]]+)\]/);
  return match ? match[1] : null;
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function AppointmentDetailModal({ apt, isGroupAccount, onClose, onUpdateStatus, onOpenStartModal, onOpenEditAmountModal, onOpenCompleteModal, isUpdating, getStatusColor, getStatusLabel }: {
  apt: any;
  isGroupAccount: boolean;
  onClose: () => void;
  onUpdateStatus: (id: string, status: string) => void;
  onOpenStartModal: (apt: any) => void;
  onOpenEditAmountModal: (apt: any) => void;
  onOpenCompleteModal: (apt: any) => void;
  isUpdating: boolean;
  getStatusColor: (s: string) => string;
  getStatusLabel: (s: string) => string;
}) {
  const [activeTab, setActiveTab] = useState<"patient" | "bundle" | "estimate" | "provider">("provider");

  const u = apt.member?.user;
  const addr = u?.addresses?.[0];
  const addrStr = addr
    ? [addr.buildingNo, addr.street1, addr.street2, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ')
    : '';

  const bundle = apt.bundle;
  const provider = apt.provider;

  const { data: estimates } = useListProviderEstimates();
  const estimateId = apt.notes?.match(/\[ESTIMATE:[^:]+:([^\]]+)\]/)?.[1];
  const estimate = estimateId ? estimates?.find((e: any) => String(e.id) === String(estimateId)) : null;
  const serviceRenderedDate = getServiceRenderedDate(apt.notes);

  const tabs = [
    { key: "provider", label: "Appointment", icon: Stethoscope },
    { key: "patient", label: "Patient", icon: User },
    ...(bundle ? [{ key: "bundle", label: "Bundle", icon: Package }] : []),
    ...(estimate ? [{ key: "estimate", label: "Package", icon: Package }] : []),
  ] as { key: "patient" | "bundle" | "estimate" | "provider"; label: string; icon: any }[];

  const cleanNotes = (apt.notes || '')
    .replace(/\[ESTIMATE:[^\]]+\]/g, '')
    .replace(/\[GROUP:[^\]]+\]/g, '')
    .replace(/\[SERVICE_RENDERED:[^\]]+\]/g, '')
    .trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-7 pt-7 pb-5 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-primary/5 to-indigo-500/5">
          <div className="flex items-center gap-4">
            {u?.profilePicture ? (
              <img src={`${API_BASE.replace(/\/v1|\/api/, '')}${u.profilePicture}`} alt={u?.displayName || u?.firstName || 'Patient'} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0 border border-slate-200 dark:border-slate-700" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-primary">
                  {(u?.displayName || u?.firstName || '?').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {u?.displayName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Patient'}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <Badge variant="outline" className={`text-[11px] px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${getStatusColor(apt.status)}`}>
                  {getStatusLabel(apt.status)}
                </Badge>
                <span className="text-xs text-slate-400 flex items-center gap-1 whitespace-nowrap">
                  <Clock className="w-3.5 h-3.5" />
                  {format(new Date(apt.createdAt), 'MMM d, yyyy · h:mm a')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors flex-shrink-0 mt-1"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 dark:border-slate-800 px-7 gap-1 pt-1 overflow-x-auto scrollbar-none">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap shrink-0 ${
                activeTab === tab.key
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-7 py-6 space-y-5">
          {/* Patient Tab */}
          {activeTab === 'patient' && (
            <div className="space-y-5">
              <SectionCard title="Personal Information" icon={User}>
                <InfoRow icon={<User className="w-4 h-4" />} label="Full Name" value={u?.displayName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—'} />
                {u?.middleName && <InfoRow icon={<User className="w-4 h-4" />} label="Middle Name" value={u.middleName} />}
                {u?.gender && <InfoRow icon={<Tag className="w-4 h-4" />} label="Gender" value={u.gender} />}
                {u?.dob && <InfoRow icon={<CalendarIcon className="w-4 h-4" />} label="Date of Birth" value={u.dob} />}
              </SectionCard>

              <SectionCard title="Contact Details" icon={Mail}>
                {u?.email && <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={<a href={`mailto:${u.email}`} className="text-primary hover:underline">{u.email}</a>} />}
                {u?.phone && <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={<a href={`tel:${u.phone}`} className="text-primary hover:underline">{u.phone}</a>} />}
                {u?.whatsapp && <InfoRow icon={<Phone className="w-4 h-4" />} label="WhatsApp" value={u.whatsapp} />}
              </SectionCard>

              {(addrStr || addr) && (
                <SectionCard title="Address" icon={MapPin}>
                  {addr?.buildingNo && <InfoRow icon={<Building2 className="w-4 h-4" />} label="Building" value={addr.buildingNo} />}
                  {addr?.street1 && <InfoRow icon={<MapPin className="w-4 h-4" />} label="Street" value={[addr.street1, addr.street2].filter(Boolean).join(', ')} />}
                  {addr?.city && <InfoRow icon={<Globe className="w-4 h-4" />} label="City / State" value={[addr.city, addr.state].filter(Boolean).join(', ')} />}
                  {addr?.zipCode && <InfoRow icon={<Hash className="w-4 h-4" />} label="ZIP Code" value={addr.zipCode} />}
                </SectionCard>
              )}
            </div>
          )}

          {/* Bundle Tab */}
          {activeTab === 'bundle' && bundle && (
            <div className="space-y-5">
              <SectionCard title="Bundle Overview" icon={Package}>
                <InfoRow icon={<Package className="w-4 h-4" />} label="Name" value={bundle.name} />
                {bundle.description && <InfoRow icon={<FileText className="w-4 h-4" />} label="Description" value={bundle.description} />}
                {bundle.category && <InfoRow icon={<Tag className="w-4 h-4" />} label="Category" value={bundle.category} />}
              </SectionCard>

              <SectionCard title="Pricing" icon={DollarSign}>
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Total Price" value={`$${Number(bundle.totalPrice || 0).toLocaleString()}`} />
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Discount" value={`${bundle.discountPercent || 0}%`} />
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <InfoRow icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Final Price" value={
                    <span className="text-lg font-bold text-emerald-600">${Number(bundle.finalPrice || apt.amount || 0).toLocaleString()}</span>
                  } />
                </div>
              </SectionCard>

              {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                <SectionCard title={`CPT Codes (${bundle.cptCodes.length})`} icon={Hash}>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-700">
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase">Code</th>
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase">Description</th>
                          <th className="text-right py-2 px-1 text-xs font-semibold text-slate-500 uppercase">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                        {bundle.cptCodes.map((cpt: any) => (
                          <tr key={cpt.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="py-2.5 px-1 font-mono font-bold text-primary text-xs">{cpt.code}</td>
                            <td className="py-2.5 px-1 text-slate-600 dark:text-slate-300 text-xs">{cpt.description}</td>
                            <td className="py-2.5 px-1 text-right font-semibold text-slate-700 dark:text-slate-300 text-xs">${Number(cpt.price || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* Estimate Tab */}
          {activeTab === 'estimate' && estimate && (
            <div className="space-y-5">
              <SectionCard title="Custom Package Overview" icon={Package}>
                <InfoRow icon={<Package className="w-4 h-4" />} label="Name" value={estimate.name || "Good Faith Estimate Package"} />
              </SectionCard>

              <SectionCard title="Pricing Breakdown" icon={DollarSign}>
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Total Charges" value={`$${Number(estimate.totalCharges || 0).toLocaleString()}`} />
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Discount" value={`${estimate.discount || 0}%`} />
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <InfoRow icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Final Charged Amount" value={
                    <span className="text-lg font-bold text-emerald-600">${Number(estimate.finalAmount || apt.amount || 0).toLocaleString()}</span>
                  } />
                </div>
              </SectionCard>
            </div>
          )}

          {/* Appointment / Provider Tab */}
          {activeTab === 'provider' && (
            <div className="space-y-5">
              <SectionCard title="Appointment Lifecycle Status" icon={CalendarIcon}>
                <InfoRow icon={<Clock className="w-4 h-4" />} label="Requested Date" value={format(new Date(apt.createdAt), 'MMMM d, yyyy · h:mm a')} />
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Charged Amount ($)" value={
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-slate-900 dark:text-white">${Number(apt.amount || 0).toLocaleString()}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { onClose(); onOpenEditAmountModal(apt); }}
                      className="h-7 px-2 text-xs gap-1 border-primary/30 text-primary hover:bg-primary/10"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Modify Amount
                    </Button>
                  </div>
                } />
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Flow Lifecycle Status" value={
                  <Badge variant="outline" className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${getStatusColor(apt.status)}`}>
                    {getStatusLabel(apt.status)}
                  </Badge>
                } />
                {serviceRenderedDate && (
                  <InfoRow icon={<CheckSquare className="w-4 h-4 text-blue-600" />} label="Date Service Rendered & Redeemed" value={
                    <span className="font-bold text-blue-600">{serviceRenderedDate}</span>
                  } />
                )}
              </SectionCard>

              {cleanNotes && (
                <SectionCard title="Notes & Special Instructions" icon={FileText}>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                    {cleanNotes}
                  </p>
                </SectionCard>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-7 py-5 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-3 bg-slate-50/50 dark:bg-slate-900">
          <Button
            variant="outline"
            className="border-slate-200 text-slate-600 hover:bg-slate-100"
            onClick={() => { onClose(); onOpenEditAmountModal(apt); }}
          >
            <Edit3 className="w-4 h-4 mr-2" /> Modify Charged Amount
          </Button>

          {apt.status === 'PENDING' && (
            <>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                onClick={() => { onUpdateStatus(apt.id, 'APPROVED'); onClose(); }}
                disabled={isUpdating}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Accept Booking
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 flex-1"
                onClick={() => { onUpdateStatus(apt.id, 'REJECTED'); onClose(); }}
                disabled={isUpdating}
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
            </>
          )}

          {apt.status === 'APPROVED' && (
            <>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex-1 gap-1.5"
                onClick={() => { onClose(); onOpenStartModal(apt); }}
              >
                <Play className="w-4 h-4" /> Start Visit (Code)
              </Button>
              <Button
                variant="outline"
                className="text-slate-600 border-slate-200 hover:bg-slate-100 flex-1"
                onClick={() => { onUpdateStatus(apt.id, 'CANCELLED'); onClose(); }}
                disabled={isUpdating}
              >
                <XCircle className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </>
          )}

          {apt.status === 'IN_PROGRESS' && (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold flex-1 gap-1.5"
              onClick={() => { onClose(); onOpenCompleteModal(apt); }}
            >
              <CheckCircle2 className="w-4 h-4" /> Complete & Redeem Bundle
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reusable subcomponents ───────────────────────────────────────────────────
function SectionCard({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 bg-white dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-700">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-slate-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
        <div className="text-sm text-slate-700 dark:text-slate-200 font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ManageAppointments() {
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [quickDate, setQuickDate] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [viewApt, setViewApt] = useState<any>(null);

  // Edit Amount Modal State
  const [editAmountModalApt, setEditAmountModalApt] = useState<any>(null);
  const [newAmountInput, setNewAmountInput] = useState<string>("");
  const [newNotesInput, setNewNotesInput] = useState<string>("");
  const [editStatusInput, setEditStatusInput] = useState<string>("");

  // Complete & Redeem Bundle Modal State
  const [completeModalApt, setCompleteModalApt] = useState<any>(null);
  const [serviceRenderedInputDate, setServiceRenderedInputDate] = useState<string>(
    () => new Date().toISOString().split('T')[0]
  );
  const [completeNotesInput, setCompleteNotesInput] = useState<string>("");

  const handleDateRangeSelect = (val: string) => {
    setQuickDate(val);
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (val === 'all') {
      setStartDate('');
      setEndDate('');
      setPage(1);
      return;
    }

    switch (val) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'this-week':
        start.setDate(start.getDate() - start.getDay());
        end.setDate(start.getDate() + 6);
        break;
      case 'last-week':
        start.setDate(start.getDate() - start.getDay() - 7);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'this-month':
        start.setDate(1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this-year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
    }

    const formatDate = (d: Date) => {
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${month}-${day}`;
    };

    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
    setPage(1);
  };

  const [user] = useState<any>(() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  });

  const isGroupAccount = user?.role === 'CLINIC' ||
    user?.provider?.practiceType?.toLowerCase() === 'group' ||
    user?.provider?.practiceType?.toLowerCase() === 'clinic';

  const [type, setType] = useState('all');
  const getFilterOptionsHook = typeof useGetProviderAppointmentFilterOptions === 'function'
    ? useGetProviderAppointmentFilterOptions
    : (() => ({ data: undefined }));
  const { data: filterOptions } = getFilterOptionsHook({});

  const providerAppointmentsResult = useGetProviderAppointments({ search, startDate, endDate, type: type === 'all' ? undefined : type, page, limit }, { query: { enabled: !isGroupAccount } });
  const groupAppointmentsResult = useGetGroupAppointments({ search, startDate, endDate, type: type === 'all' ? undefined : type, page, limit }, { query: { enabled: isGroupAccount } });

  const { data: estimatesData } = useListProviderEstimates();
  const activeResult = isGroupAccount ? groupAppointmentsResult : providerAppointmentsResult;
  const { data: appointmentsData, isLoading, error } = activeResult;
  const updateStatus = useUpdateAppointmentStatus();
  const startAppointmentMutation = useStartAppointment();
  const [startModalApt, setStartModalApt] = useState<any>(null);
  const [startCodeInput, setStartCodeInput] = useState('');
  const { toast } = useToast();

  const rawAppointments = appointmentsData?.items || [];
  const totalPages = appointmentsData?.totalPages || 1;
  const totalCount = appointmentsData?.totalCount || 0;

  // Status Filtered List
  const appointments = useMemo(() => {
    if (statusFilter === 'all') return rawAppointments;
    if (statusFilter === 'PENDING') return rawAppointments.filter((a: any) => a.status === 'PENDING');
    if (statusFilter === 'APPROVED') return rawAppointments.filter((a: any) => a.status === 'APPROVED');
    if (statusFilter === 'IN_PROGRESS') return rawAppointments.filter((a: any) => a.status === 'IN_PROGRESS');
    if (statusFilter === 'COMPLETED') return rawAppointments.filter((a: any) => a.status === 'COMPLETED');
    if (statusFilter === 'CANCELLED') return rawAppointments.filter((a: any) => a.status === 'CANCELLED' || a.status === 'REJECTED');
    return rawAppointments;
  }, [rawAppointments, statusFilter]);

  // Metrics calculation
  const metrics = useMemo(() => {
    let pending = 0;
    let approved = 0;
    let inProgress = 0;
    let completed = 0;
    let totalRevenue = 0;

    rawAppointments.forEach((a: any) => {
      totalRevenue += Number(a.amount || 0);
      if (a.status === 'PENDING') pending++;
      else if (a.status === 'APPROVED') approved++;
      else if (a.status === 'IN_PROGRESS') inProgress++;
      else if (a.status === 'COMPLETED') completed++;
    });

    return { pending, approved, inProgress, completed, totalRevenue };
  }, [rawAppointments]);

  const handleUpdateStatus = (id: string, status: string) => {
    updateStatus.mutate({ id, status }, {
      onSuccess: () => {
        toast({ title: "Status Updated", description: `Appointment has been marked as ${status.toLowerCase()}.` });
      },
      onError: () => {
        toast({ variant: "destructive", title: "Error", description: "Could not update appointment status." });
      }
    });
  };

  const handleOpenEditAmountModal = (apt: any) => {
    setEditAmountModalApt(apt);
    setNewAmountInput(String(apt.amount || 0));
    const cleanNotesText = (apt.notes || '')
      .replace(/\[ESTIMATE:[^\]]+\]/g, '')
      .replace(/\[GROUP:[^\]]+\]/g, '')
      .replace(/\[SERVICE_RENDERED:[^\]]+\]/g, '')
      .trim();
    setNewNotesInput(cleanNotesText);
    setEditStatusInput(apt.status || "APPROVED");
  };

  const handleSaveEditAmount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAmountModalApt) return;

    const amountNum = parseFloat(newAmountInput);
    if (isNaN(amountNum) || amountNum < 0) {
      toast({ variant: "destructive", title: "Invalid Amount", description: "Please enter a valid non-negative number for the amount." });
      return;
    }

    const systemTags = (editAmountModalApt.notes || '').match(/\[(ESTIMATE|GROUP|SERVICE_RENDERED):[^\]]+\]/g) || [];
    const updatedNotes = [...systemTags, newNotesInput.trim()].filter(Boolean).join(' ');

    updateStatus.mutate({
      id: editAmountModalApt.id,
      amount: amountNum,
      notes: updatedNotes,
      status: editStatusInput
    }, {
      onSuccess: () => {
        toast({ title: "Appointment Updated", description: "Updated appointment amount and status successfully." });
        setEditAmountModalApt(null);
      },
      onError: () => {
        toast({ variant: "destructive", title: "Error", description: "Failed to update appointment details." });
      }
    });
  };

  const handleOpenCompleteModal = (apt: any) => {
    setCompleteModalApt(apt);
    const existingDate = getServiceRenderedDate(apt.notes);
    setServiceRenderedInputDate(existingDate || new Date().toISOString().split('T')[0]);
    const cleanNotesText = (apt.notes || '')
      .replace(/\[ESTIMATE:[^\]]+\]/g, '')
      .replace(/\[GROUP:[^\]]+\]/g, '')
      .replace(/\[SERVICE_RENDERED:[^\]]+\]/g, '')
      .trim();
    setCompleteNotesInput(cleanNotesText);
  };

  const handleSaveCompleteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!completeModalApt) return;

    updateStatus.mutate({
      id: completeModalApt.id,
      status: 'COMPLETED',
      serviceRenderedDate: serviceRenderedInputDate,
      notes: completeNotesInput
    }, {
      onSuccess: () => {
        toast({ title: "Service Rendered & Bundle Redeemed", description: `Appointment completed with Service Rendered Date: ${serviceRenderedInputDate}` });
        setCompleteModalApt(null);
      },
      onError: () => {
        toast({ variant: "destructive", title: "Error", description: "Failed to complete appointment and redeem bundle." });
      }
    });
  };

  const handleStartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startModalApt || !startCodeInput.trim()) return;

    startAppointmentMutation.mutate({
      id: startModalApt.id,
      verificationCode: startCodeInput.trim()
    }, {
      onSuccess: () => {
        toast({ title: "Appointment Started", description: "Verification code match! The visit is now in progress." });
        setStartModalApt(null);
        setStartCodeInput("");
      },
      onError: (err: any) => {
        toast({ variant: "destructive", title: "Verification Failed", description: err.message || "Invalid verification code. Please check with the patient." });
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300';
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300';
      case 'IN_PROGRESS': return 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-300';
      case 'REJECTED':
      case 'CANCELLED': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300';
      case 'COMPLETED': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300';
      default: return 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-300';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'New Request (Purchased/Requested)';
      case 'APPROVED': return 'Awaiting Service (Accepted/Modified)';
      case 'IN_PROGRESS': return 'In Progress (Active Visit)';
      case 'COMPLETED': return 'Completed & Redeemed';
      case 'CANCELLED': return 'Cancelled';
      case 'REJECTED': return 'Declined';
      default: return status;
    }
  };

  return (
    <ProviderLayout>
      <div className="w-full flex flex-col min-h-[calc(100vh-8rem)] space-y-4 sm:space-y-5 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-2xl flex-shrink-0">
              <CalendarIcon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Appointments</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1 text-xs sm:text-sm">
                Manage appointment lifecycle: Accept/Modify, Service in progress, and Bundle Redemption.
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="inline-flex items-center gap-1.5 p-1.5 rounded-xl bg-slate-100 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700/80 shadow-2xs shrink-0 self-start sm:self-center">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === "table"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs border border-slate-200/80 dark:border-slate-700/80 font-bold"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
              }`}
            >
              <LayoutList className="w-4 h-4 shrink-0" />
              <span>Table List</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                viewMode === "grid"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs border border-slate-200/80 dark:border-slate-700/80 font-bold"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
              }`}
            >
              <LayoutGrid className="w-4 h-4 shrink-0" />
              <span>Cards</span>
            </button>
          </div>
        </div>

        {/* Quick Metrics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 flex flex-col justify-between shadow-2xs hover:shadow-xs hover:border-slate-300 dark:hover:border-slate-700 transition-all">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Bookings</span>
            <span className="text-2xl font-bold text-slate-900 dark:text-white mt-1.5">{totalCount}</span>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 flex flex-col justify-between shadow-2xs hover:shadow-xs hover:border-amber-500/30 transition-all">
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400 leading-tight">1. New Requests</span>
            <span className="text-2xl font-bold text-amber-700 dark:text-amber-400 mt-1.5">{metrics.pending}</span>
          </div>
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex flex-col justify-between shadow-2xs hover:shadow-xs hover:border-emerald-500/30 transition-all">
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 leading-tight">2. Awaiting Service</span>
            <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 mt-1.5">{metrics.approved}</span>
          </div>
          <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-4 flex flex-col justify-between shadow-2xs hover:shadow-xs hover:border-indigo-500/30 transition-all">
            <span className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 leading-tight">3. In Progress</span>
            <span className="text-2xl font-bold text-indigo-700 dark:text-indigo-400 mt-1.5">{metrics.inProgress}</span>
          </div>
          <div className="col-span-2 md:col-span-1 bg-blue-500/5 border border-blue-500/20 rounded-2xl p-4 flex flex-col justify-between shadow-2xs hover:shadow-xs hover:border-blue-500/30 transition-all">
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 leading-tight">4. Completed & Redeemed</span>
            <span className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1.5">{metrics.completed}</span>
          </div>
        </div>

        {/* Filter Controls Row */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 space-y-4 shadow-xs">
          {/* Appointment Flow Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none border-b border-slate-100 dark:border-slate-800">
            {[
              { id: 'all', label: 'All Appointments', count: rawAppointments.length },
              { id: 'PENDING', label: 'New Requests', count: metrics.pending },
              { id: 'APPROVED', label: 'Awaiting Service', count: metrics.approved },
              { id: 'IN_PROGRESS', label: 'In Progress', count: metrics.inProgress },
              { id: 'COMPLETED', label: 'Completed & Redeemed', count: metrics.completed },
              { id: 'CANCELLED', label: 'Cancelled / Declined', count: rawAppointments.filter((a: any) => a.status === 'CANCELLED' || a.status === 'REJECTED').length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setStatusFilter(tab.id); setPage(1); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap shrink-0 transition ${
                  statusFilter === tab.id
                    ? 'bg-primary text-primary-foreground shadow-xs font-bold'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold min-w-5 text-center shrink-0 ${
                  statusFilter === tab.id ? 'bg-primary-foreground text-primary' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-2.5 sm:gap-3 items-stretch lg:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search patient name, appointment ID, or service..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 h-10 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-xs focus-visible:ring-primary/20"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5 w-full lg:w-auto shrink-0">
              <Select value={type} onValueChange={val => { setType(val); setPage(1); }}>
                <SelectTrigger className="h-10 w-full sm:flex-1 lg:w-48 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-xs hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                  <SelectValue placeholder="Service Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Service Types</SelectItem>
                  <SelectItem value="individual">Individual Visit</SelectItem>
                  {filterOptions?.map((opt: any) => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={quickDate} onValueChange={handleDateRangeSelect}>
                <SelectTrigger className="h-10 w-full sm:flex-1 lg:w-48 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-xs hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                  <SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="yesterday">Yesterday</SelectItem>
                  <SelectItem value="this-week">This Week</SelectItem>
                  <SelectItem value="last-week">Last Week</SelectItem>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="this-year">This Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Content Body */}
        {isLoading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-primary" />
            <p className="text-sm font-medium">Loading appointments list...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Failed to load appointments.</div>
        ) : appointments.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-xs">
            <AlertCircle className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">No appointments found</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm max-w-md mx-auto">
              {search || startDate || endDate || statusFilter !== 'all'
                ? "Try clearing or adjusting your search filters."
                : "You haven't received any booking requests yet."}
            </p>
          </div>
        ) : viewMode === "table" ? (
          /* ── COLUMNAR TABLE LIST VIEW ── */
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs flex-1 flex flex-col justify-between">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs min-w-[920px] xl:min-w-full">
                <thead>
                  <tr className="bg-slate-50/90 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px] backdrop-blur-xs">
                    <th className="py-3.5 px-4 min-w-[180px]">Patient</th>
                    <th className="py-3.5 px-4 min-w-[200px]">Service & CPT Codes</th>
                    <th className="py-3.5 px-4 min-w-[160px] whitespace-nowrap">Requested / Service Date</th>
                    <th className="py-3.5 px-4 text-right min-w-[110px] whitespace-nowrap">Amount ($)</th>
                    <th className="py-3.5 px-4 text-center min-w-[210px] whitespace-nowrap">Lifecycle Status</th>
                    <th className="py-3.5 px-4 text-right min-w-[170px] whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                  {appointments.map((apt: any) => {
                    const u = apt.member?.user;
                    const name = u?.displayName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Patient';
                    const serviceRenderedDate = getServiceRenderedDate(apt.notes);

                    let cptCodes: any[] = [];
                    if (apt.bundle?.cptCodes && Array.isArray(apt.bundle.cptCodes) && apt.bundle.cptCodes.length > 0) {
                      cptCodes = apt.bundle.cptCodes;
                    } else {
                      const estId = apt.notes?.match(/\[ESTIMATE:[^:]+:([^\]]+)\]/)?.[1];
                      if (estId && estimatesData) {
                        const est = estimatesData.find((e: any) => String(e.id) === String(estId));
                        if (est?.items && Array.isArray(est.items)) {
                          cptCodes = est.items;
                        }
                      }
                    }

                    const serviceName = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');

                    return (
                      <tr key={apt.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                        {/* Patient */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
                              {name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-900 dark:text-white truncate" title={name}>{name}</p>
                              {u?.email && <p className="text-[11px] text-slate-400 truncate max-w-[190px] sm:max-w-xs" title={u.email}>{u.email}</p>}
                            </div>
                          </div>
                        </td>

                        {/* Service & CPT */}
                        <td className="py-3.5 px-4">
                          <p className="font-semibold text-slate-800 dark:text-slate-200 max-w-[220px] truncate" title={serviceName}>{serviceName}</p>
                          {cptCodes && cptCodes.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap mt-1">
                              {cptCodes.slice(0, 3).map((c: any, idx: number) => {
                                const codeStr = c.code || c.cptCode || (typeof c === 'string' ? c : '');
                                if (!codeStr) return null;
                                return (
                                  <span key={idx} className="font-mono text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-md font-bold whitespace-nowrap">
                                    {codeStr}
                                  </span>
                                );
                              })}
                              {cptCodes.length > 3 && (
                                <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">+{cptCodes.length - 3} more</span>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Date & Time */}
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                            <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{format(new Date(apt.createdAt), 'MMM d, yyyy')}</span>
                          </div>
                          {serviceRenderedDate ? (
                            <span className="text-[11px] text-blue-600 font-bold block mt-0.5 whitespace-nowrap">
                              Serviced: {serviceRenderedDate}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400 block whitespace-nowrap">{format(new Date(apt.createdAt), 'h:mm a')}</span>
                          )}
                        </td>

                        {/* Amount */}
                        <td className="py-3.5 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                            <span className="font-bold text-slate-900 dark:text-white text-sm">
                              ${Number(apt.amount || 0).toLocaleString()}
                            </span>
                            <button
                              onClick={() => handleOpenEditAmountModal(apt)}
                              className="p-1 text-slate-400 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                              title="Modify Amount / Notes"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 text-center whitespace-nowrap">
                          <Badge variant="outline" className={`text-[11px] px-3 py-1 rounded-full font-semibold whitespace-nowrap inline-flex items-center justify-center leading-normal ${getStatusColor(apt.status)}`}>
                            {getStatusLabel(apt.status)}
                          </Badge>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-slate-500 hover:text-primary hover:bg-primary/10 rounded-xl"
                              onClick={() => setViewApt(apt)}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>

                            {/* Pending Action Buttons */}
                            {apt.status === 'PENDING' && (
                              <>
                                <Button
                                  size="sm"
                                  className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1 rounded-xl"
                                  onClick={() => handleUpdateStatus(apt.id, 'APPROVED')}
                                  disabled={updateStatus.isPending}
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50 text-xs rounded-xl"
                                  onClick={() => handleUpdateStatus(apt.id, 'REJECTED')}
                                  disabled={updateStatus.isPending}
                                >
                                  <XCircle className="w-3.5 h-3.5" /> Reject
                                </Button>
                              </>
                            )}

                            {/* Approved Actions */}
                            {apt.status === 'APPROVED' && (
                              <Button
                                size="sm"
                                className="h-8 px-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs gap-1 rounded-xl"
                                onClick={() => {
                                  setStartModalApt(apt);
                                  setStartCodeInput("");
                                }}
                              >
                                <Play className="w-3.5 h-3.5" /> Start Visit
                              </Button>
                            )}

                            {/* In Progress Actions */}
                            {apt.status === 'IN_PROGRESS' && (
                              <Button
                                size="sm"
                                className="h-8 px-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1 rounded-xl"
                                onClick={() => handleOpenCompleteModal(apt)}
                                disabled={updateStatus.isPending}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Complete & Redeem
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-center">
              <ListPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
            </div>
          </div>
        ) : (
          /* ── GRID / CARD VIEW ── */
          <div className="space-y-4 flex-1 flex flex-col justify-between">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {appointments.map((apt: any) => {
                const u = apt.member?.user;
                const name = u?.displayName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Patient';
                const serviceName = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');
                const serviceRenderedDate = getServiceRenderedDate(apt.notes);

                let cptCodes: any[] = [];
                if (apt.bundle?.cptCodes && Array.isArray(apt.bundle.cptCodes) && apt.bundle.cptCodes.length > 0) {
                  cptCodes = apt.bundle.cptCodes;
                } else {
                  const estId = apt.notes?.match(/\[ESTIMATE:[^:]+:([^\]]+)\]/)?.[1];
                  if (estId && estimatesData) {
                    const est = estimatesData.find((e: any) => String(e.id) === String(estId));
                    if (est?.items && Array.isArray(est.items)) {
                      cptCodes = est.items;
                    }
                  }
                }

                return (
                  <div key={apt.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/90 dark:border-slate-800 p-4 sm:p-5 shadow-2xs hover:shadow-xs hover:border-slate-300 dark:hover:border-slate-700 transition-all flex flex-col justify-between min-h-[220px] gap-4">
                    <div>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2.5 mb-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-slate-900 dark:text-white truncate" title={name}>{name}</h3>
                            {u?.email && <p className="text-xs text-slate-400 truncate" title={u.email}>{u.email}</p>}
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[11px] px-2.5 py-1 rounded-xl sm:rounded-full font-semibold shrink-0 text-center leading-normal max-w-full sm:max-w-[210px] break-words ${getStatusColor(apt.status)}`}>
                          {getStatusLabel(apt.status)}
                        </Badge>
                      </div>

                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-2">{serviceName}</p>

                      {cptCodes && cptCodes.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap mt-1.5">
                          {cptCodes.slice(0, 4).map((c: any, idx: number) => {
                            const codeStr = c.code || c.cptCode || (typeof c === 'string' ? c : '');
                            if (!codeStr) return null;
                            return (
                              <span key={idx} className="font-mono text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-md font-bold whitespace-nowrap">
                                {codeStr}
                              </span>
                            );
                          })}
                          {cptCodes.length > 4 && (
                            <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">+{cptCodes.length - 4} more</span>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                        <span>{format(new Date(apt.createdAt), 'MMM d, yyyy · h:mm a')}</span>
                      </p>
                      {serviceRenderedDate && (
                        <p className="text-xs font-bold text-blue-600 mt-1 flex items-center gap-1">
                          <CheckSquare className="w-3.5 h-3.5 shrink-0" /> Date Service Rendered: {serviceRenderedDate}
                        </p>
                      )}
                    </div>

                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <span className="text-[11px] text-slate-400 block">Charged Amount</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-lg font-bold text-slate-900 dark:text-white">${Number(apt.amount || 0).toLocaleString()}</span>
                          <button
                            onClick={() => handleOpenEditAmountModal(apt)}
                            className="p-1 text-slate-400 hover:text-primary transition rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                            title="Modify Amount"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
                        <Button variant="outline" size="sm" onClick={() => setViewApt(apt)} className="text-xs h-8 rounded-xl px-2.5">
                          <Eye className="w-3.5 h-3.5 mr-1" /> Details
                        </Button>

                        {apt.status === 'PENDING' && (
                          <>
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 rounded-xl px-2.5 gap-1" onClick={() => handleUpdateStatus(apt.id, 'APPROVED')} disabled={updateStatus.isPending}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-8 rounded-xl px-2.5 gap-1" onClick={() => handleUpdateStatus(apt.id, 'REJECTED')} disabled={updateStatus.isPending}>
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </Button>
                          </>
                        )}
                        {apt.status === 'APPROVED' && (
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 rounded-xl px-2.5 gap-1" onClick={() => { setStartModalApt(apt); setStartCodeInput(""); }}>
                            <Play className="w-3.5 h-3.5" /> Start Visit
                          </Button>
                        )}
                        {apt.status === 'IN_PROGRESS' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 rounded-xl px-2.5 gap-1" onClick={() => handleOpenCompleteModal(apt)}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Complete & Redeem
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-3.5 shadow-2xs flex justify-center">
              <ListPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} totalCount={totalCount} limit={limit} onLimitChange={setLimit} />
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {viewApt && (
        <AppointmentDetailModal
          apt={viewApt}
          isGroupAccount={isGroupAccount}
          onClose={() => setViewApt(null)}
          onUpdateStatus={handleUpdateStatus}
          onOpenStartModal={(a) => {
            setStartModalApt(a);
            setStartCodeInput("");
          }}
          onOpenEditAmountModal={(a) => handleOpenEditAmountModal(a)}
          onOpenCompleteModal={(a) => handleOpenCompleteModal(a)}
          isUpdating={updateStatus.isPending}
          getStatusColor={getStatusColor}
          getStatusLabel={getStatusLabel}
        />
      )}

      {/* Modify Paid / Charged Amount Modal */}
      <Dialog open={!!editAmountModalApt} onOpenChange={(open) => !open && setEditAmountModalApt(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
              <Edit3 className="w-5 h-5 text-primary" /> Modify Charged Amount
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Adjust the charged amount, booking status, or provider notes for this appointment.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveEditAmount} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Charged Amount ($)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newAmountInput}
                  onChange={(e) => setNewAmountInput(e.target.value)}
                  className="pl-8 font-bold text-base h-11 rounded-xl border-slate-200 dark:border-slate-800"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Booking Lifecycle Status
              </label>
              <Select value={editStatusInput} onValueChange={setEditStatusInput}>
                <SelectTrigger className="h-10 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-xs font-semibold">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">1. New Request (PENDING)</SelectItem>
                  <SelectItem value="APPROVED">2. Awaiting Service (APPROVED / Accepted)</SelectItem>
                  <SelectItem value="IN_PROGRESS">3. In Progress (IN_PROGRESS)</SelectItem>
                  <SelectItem value="COMPLETED">4. Completed & Redeemed (COMPLETED)</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  <SelectItem value="REJECTED">Declined / Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Provider Notes / Communication
              </label>
              <textarea
                rows={3}
                placeholder="Add special notes or payment details..."
                value={newNotesInput}
                onChange={(e) => setNewNotesInput(e.target.value)}
                className="w-full text-xs p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>

            <DialogFooter className="sm:justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" className="rounded-xl text-xs" onClick={() => setEditAmountModalApt(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold text-xs gap-1.5"
                disabled={updateStatus.isPending}
              >
                {updateStatus.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving Changes...</>
                ) : (
                  <><CheckCircle2 className="w-3.5 h-3.5" /> Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Visit & Redeem Bundle Modal */}
      <Dialog open={!!completeModalApt} onOpenChange={(open) => !open && setCompleteModalApt(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
              <Sparkles className="w-5 h-5 text-blue-600" /> Complete Visit & Redeem Bundle
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Mark this service as rendered, redeem the bundle, and record the official Date Service Rendered.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveCompleteSubmit} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5 text-blue-600" /> Date Service Rendered
              </label>
              <Input
                type="date"
                value={serviceRenderedInputDate}
                onChange={(e) => setServiceRenderedInputDate(e.target.value)}
                className="font-semibold text-sm h-11 rounded-xl border-slate-200 dark:border-slate-800"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                Completion Notes (Optional)
              </label>
              <textarea
                rows={3}
                placeholder="Add final clinical or bundle redemption notes..."
                value={completeNotesInput}
                onChange={(e) => setCompleteNotesInput(e.target.value)}
                className="w-full text-xs p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            <DialogFooter className="sm:justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" className="rounded-xl text-xs" onClick={() => setCompleteModalApt(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-xs gap-1.5"
                disabled={updateStatus.isPending || !serviceRenderedInputDate}
              >
                {updateStatus.isPending ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Completing Visit...</>
                ) : (
                  <><CheckCircle2 className="w-3.5 h-3.5" /> Complete Visit & Redeem</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Start Appointment Modal - Patient Verification Code */}
      <Dialog open={!!startModalApt} onOpenChange={(open) => !open && setStartModalApt(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
              <Key className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Start Appointment
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Ask the patient for their 6-digit verification code to start the visit.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleStartSubmit} className="space-y-4 my-2">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                6-Digit Verification Code
              </label>
              <Input
                type="text"
                maxLength={6}
                placeholder="e.g. 782941"
                value={startCodeInput}
                onChange={(e) => setStartCodeInput(e.target.value.replace(/[^0-9a-zA-Z]/g, ''))}
                className="text-center font-mono text-2xl font-bold tracking-widest h-12 rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
            </div>

            <DialogFooter className="sm:justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" className="rounded-xl" onClick={() => setStartModalApt(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold gap-1.5"
                disabled={startAppointmentMutation.isPending || !startCodeInput.trim()}
              >
                {startAppointmentMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</>
                ) : (
                  <><Play className="w-4 h-4" /> Verify & Start</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
